import { Form, message, Spin } from "antd"
import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import "../../styles/Pages/createstore.css"
import StoreResubmission from "./StoreResubmission"
import { Store } from "../../Apis/storeApis"
import { useDispatch, useSelector } from "react-redux"
import { createFormData } from "../../helper/createFormData"
import StoreFormFields from "../Common/StoreFormInputFields"
import { fetchUserDetails } from "../../ReduxToolKit/Slice/UserSlice"
import { translations } from "../../utils/Translation/staticTranslation"
import { getStoreApiErrorMessage } from "../../helper/storeApiErrorMessage"
import { getLocalizedPath } from "../../utils/authLanguageRoutes"
import { getAppLanguage } from "../../utils/getAppLanguage"

const StoreForm = ({ mode }) => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { storeId } = useParams()
  const dispatch = useDispatch()

  const user = useSelector((state) => state.user.user)

  const [storeName, setStoreName] = useState("")
  const [storeNameArabic, setStoreNameArabic] = useState("")
  const [loading, setLoading] = useState(false)
  const [activeStore, setActiveStore] = useState(false)

  const lang = getAppLanguage()
  const createStore = translations[lang]?.StorePage?.createStore
  const store = translations[lang]?.StorePage
  const common_error_msg = translations[lang]?.common_error_msg
  const goToStores = () => navigate(getLocalizedPath("/stores", lang))

  const onFinish = async (values) => {
    setLoading(true)

    const formData = createFormData(values, user)
    formData.append("language_choice", lang.toUpperCase())

    // ✅ Add Arabic name explicitly
    if (values.store_name_arabic) {
      formData.append("store_name_arabic", values.store_name_arabic)
    }

    formData.append("language_choice", lang.toUpperCase())

    try {
      const response = await Store.createStore(formData)
      if (response.status === 201) {
        !user.store_data && dispatch(fetchUserDetails())
        message.success(store?.store_creation_msg)
        goToStores()

        form.resetFields()
      }
    } catch (error) {
      message.error(
        getStoreApiErrorMessage(error.response?.data, lang, {
          generic: common_error_msg?.store_submit_failed,
        })
      )
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchStoreData = async () => {
      if (mode !== "Create" && storeId) {
        try {
          setLoading(true)
          let response
          if (mode === "Update") {
            response = await Store.getApprovedStoreById(storeId)
          } else if (mode === "Resubmit") {
            response = await Store.getRejectedStoreById(storeId)
          }
          if (response.status === 200) {
            // Pre-fill form with existing data
            form.setFieldsValue({
              prefered_store_name: response.data.prefered_store_name,
              store_name_arabic: response.data.store_name_arabic || "", // ✅ Correct mapping
              store_name: response.data.store_name || "",
              store_type: response.data.provider || "",
              name_arabic: response.data.store_name_arabic || "",
              store_description: response.data.store_description || "",
              registration_number: response.data.registration_number || "",
              business_url: response.data.business_url || "",
              maroof_link: response.data.maroof_link || "",
              location: response.data.location || "",
              vat_number: response.data.vat_number || "",
              countries: response.data.countries || [],
              logo: response.data.logo || "",
              registration_certificate:
                response.data.registration_certificate || "",
              vat_certificate: response.data.vat_certificate,
            })
            setActiveStore(response.data.active_status)
            // Set store name for preview
            setStoreName(response.data.prefered_store_name || "")
          }
        } catch (error) {
          message.error(common_error_msg?.failed_to_load_store)
        } finally {
          setLoading(false)
        }
      }
    }

    fetchStoreData()
  }, [mode, storeId, form])

  const UpdateReSubmitStore = async (values) => {
    setLoading(true)
    const formData = new FormData()
    let formDataResubmit

    if (mode === "Update") {
      formData.append("logo", values.logo || "")
      formData.append("prefered_store_name", values.prefered_store_name)
      formData.append("store_name_arabic", values.store_name_arabic || "") // ✅ Arabic Name

      if (values.logo) formData.append("logo", values.logo)
      formData.append("active_status", activeStore)
      formData.append("store_description", values.store_description)
      formData.append(
        "language_choice",
        (localStorage.getItem("language") || "ar").toUpperCase()
      )
      values.countries.forEach((country) => {
        formData.append("countries", country)
      })
    } else if (mode === "Resubmit") {
      formDataResubmit = createFormData(values, user)
    }

    let response
    try {
      if (mode === "Update") {
        response = await Store.updateStore(formData, storeId)
      } else if (mode === "Resubmit") {
        response = await Store.updateRejectedRequest(formDataResubmit, storeId)
      }

      if (response.status === 200 || response.status === 201) {
        message.success(`Store ${mode} successfully`)
        goToStores()
        form.resetFields()
      }
    } catch (error) {
      message.error(
        getStoreApiErrorMessage(error.response?.data, lang, {
          generic: common_error_msg?.store_submit_failed,
        })
      )
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="d-flex mb-18">
        <div>
          <img
            src="/icons/backpagearrow.svg"
            alt=""
            style={{ transform: lang === "ar" ? "rotateY(180deg)" : "none" }}
            className={`${lang === "ar" ? "ml-10" : "mr-10"}`}
            onClick={() => goToStores()}
          />
        </div>
        <div>
          <h1 className="text-141414 text-20">{createStore[mode]}</h1>
          <p className="text-504943 text-13">
            {store?.Store}{" "}
            <span className="text-85849D">/ {createStore[mode]}</span>
          </p>
        </div>
      </div>

      <div className="main-content-container">
        {mode === "Resubmit" && (
          <StoreResubmission rejectedRequestId={storeId} />
        )}
        <Form
          layout="vertical"
          onFinish={mode === "Create" ? onFinish : UpdateReSubmitStore}
          form={form}
          encType="multipart/form-data"
        >
          {/* Using the reusable StoreFormFields component */}
          <StoreFormFields
            form={form}
            mode={mode}
            storeName={storeName}
            setStoreName={setStoreName}
            activeStore={activeStore}
            setActiveStore={setActiveStore}
            storeNameArabic={storeNameArabic}
            setStoreNameArabic={setStoreNameArabic}
          />

          <div className="py-20">
            <div
              className={`d-flex ${
                mode === "Update"
                  ? "justify-content-between"
                  : "justify-content-end"
              }`}
            >
              {/* Submit button is outside the reusable component */}
              <button
                type="submit"
                className={`add-store ${loading && "not-allowed"}`}
                disabled={loading}
              >
                {loading ? (
                  <Spin size="small" />
                ) : mode === "Update" ? (
                  createStore?.Update
                ) : mode === "Create" ? (
                  createStore?.submit_request
                ) : (
                  createStore?.resubmit_request
                )}
              </button>
            </div>
          </div>
        </Form>
        {/* <div className="d-flex justify-content-end">
          <button className="cancel-btn mr-8">Cancel</button>
          <button className="Add-btn">Add</button>
        </div> */}
      </div>
    </div>
  )
}

export default StoreForm
